import { rashifalReadingHref } from "@/lib/rashifal-reading-href";

/**
 * The /transits/ and /hi/transits/ "transits from your Moon sign" check: its
 * analytics placements, slot floors and links, and the Hindi card's controls.
 *
 * The hubs show where the nine grahas are today. The house-by-house reading
 * from a Moon sign lives on the daily rashifal hub, one card per sign (until
 * 2026-10-25 each sign's own /rashifal/<sign>/ page, which merged into the
 * hub), which is why #2563 left these hubs without a per-sign reading. This
 * check does not repeat those readings: it settles the reader's Chandra rashi
 * from their birth details (the /rashi/<sign>/ check's engine call and
 * verdict, reused) and hands them to that sign's card on the hub.
 *
 * The English form is the rashi check's (components/rashi/rashi-check-form.ts),
 * imported unchanged. The Hindi controls below are Devanagari Hindi, like
 * BirthDetailsForm.i18n.ts (owner decision 2026-10-28, replacing the
 * 2026-10-08 Hinglish rule; tests/hindi-register.test.ts guards this file).
 * The section's heading and lead and the answer's text are page copy in their
 * components (TransitsMoonCheckSection.tsx, TransitsMoonAnswerHi.tsx).
 */

/** free_tool_started and calculator_used placement for the English hub's check. */
export const TRANSITS_MOON_CHECK_PLACEMENT = "transits_hub_moon_check";

/** free_tool_started and calculator_used placement for the Hindi hub's check. */
export const TRANSITS_MOON_CHECK_HI_PLACEMENT = "hi_transits_hub_moon_check";

/**
 * The slot floors, from the server HTML: each locale's tallest measured state,
 * which every state fills (TransitsMoonCheckLazy has the numbers).
 */
export const TRANSITS_MOON_CHECK_RESERVE = "min-h-[600px] sm:min-h-[500px]";
export const TRANSITS_MOON_CHECK_HI_RESERVE = "min-h-[560px] sm:min-h-[480px]";

/** A sign's daily reading on the /rashifal/ hub, the answer's next step. */
export function transitReadingHref(rashi: string): string {
  return rashifalReadingHref(rashi);
}

/** The same reading on the Hindi hub: a Hindi page links Hindi pages. */
export function hiTransitReadingHref(rashi: string): string {
  return rashifalReadingHref(rashi, "hi");
}

export const TRANSITS_MOON_CHECK_HI_COPY = {
  // Shorter than the daily check's "अपनी राशि जाँचें": the Hindi hub's page
  // gutter is 20 px, so at 320 px wide that label wrapped to two lines, and the
  // one-line loading label then moved the submit under the reader's finger
  // (measured 2026-10-15; the 2026-10-14 graha "jaanein" lesson).
  submit: "राशि जाँचें",
  // What the check does without a time, from the engine's day windows (the
  // rashi check's RASHI_TIME_UNKNOWN_EXPLAINER, in Hindi).
  timeUnknownExplainer:
    "कोई बात नहीं: जाँच आपकी जन्म तिथि और जन्म स्थान से होती है। अगर उस दिन चंद्रमा एक ही राशि में रहा, तो तिथि से ही राशि तय हो जाती है। अगर उस दिन राशि बदली, तो बदलाव का समय और उसके दोनों तरफ़ की राशि दिखेगी।",
  unavailable: "गणना नहीं हो पाई — कृपया दोबारा कोशिश करें।",
  unreadable: "यह नतीजा पढ़ा नहीं जा सका। कृपया दोबारा कोशिश करें।",
  checkAnother: "दूसरी जन्म तिथि जाँचें",
  addTime: "जन्म समय डालें",
} as const;

/** The answer's link: "वृषभ का आज का गोचर फल पढ़ें", the sign in its Hindi name. */
export function transitHiLinkLabel(rashiName: string): string {
  return `${rashiName} का आज का गोचर फल पढ़ें`;
}

/**
 * The Hindi form props, shared by the server-rendered shell
 * (TransitsMoonCheckLazy), the island's BirthDetailsForm and
 * BirthToolFormShell.parity.test.tsx, so every frame before and after the
 * island loads reads the same words. No name field (the answer is the
 * reader's Moon sign) and no form heading (the section's heading names the
 * card; BirthDetailsForm keys its input ids on the heading, "-default"
 * without one). The birth time stays optional: the date alone settles the
 * sign on most days (lib/rashi-check.ts).
 */
export const TRANSITS_MOON_CHECK_HI_FORM = {
  language: "hi",
  submitLabel: TRANSITS_MOON_CHECK_HI_COPY.submit,
  collectName: false,
} as const;
